import type { FlipkartAdsAdAccount } from '@/api/client';
import type { LinkedAccountRow } from '@/api/managerAccounts';

/** ISO-style country/region code used as the Accounts page group key (e.g. US, MX, DE). */
export type AdvertisingAccountRegion = string;

export type AdvertisingAccountRegionMeta = {
  id: AdvertisingAccountRegion;
  label: string;
};

/** Fixed Flipkart marketplace buckets. */
export const ADVERTISING_ACCOUNT_REGIONS: AdvertisingAccountRegionMeta[] = [
  { id: 'IN', label: 'India' },
  { id: 'EU', label: 'Europe' },
  { id: 'US', label: 'United States' },
];

/**
 * Amazon Ads marketplaces from the official bid / budget constraint tables.
 * Used for Accounts page section labels and preferred sort order.
 */
export const ADVERTISING_MARKETPLACE_COUNTRIES: AdvertisingAccountRegionMeta[] = [
  { id: 'US', label: 'United States' },
  { id: 'CA', label: 'Canada' },
  { id: 'MX', label: 'Mexico' },
  { id: 'BR', label: 'Brazil' },
  { id: 'UK', label: 'United Kingdom' },
  { id: 'DE', label: 'Germany' },
  { id: 'FR', label: 'France' },
  { id: 'ES', label: 'Spain' },
  { id: 'IT', label: 'Italy' },
  { id: 'NL', label: 'Netherlands' },
  { id: 'BE', label: 'Belgium' },
  { id: 'IE', label: 'Ireland' },
  { id: 'SE', label: 'Sweden' },
  { id: 'PL', label: 'Poland' },
  { id: 'TR', label: 'Turkey' },
  { id: 'EG', label: 'Egypt' },
  { id: 'SA', label: 'Saudi Arabia' },
  { id: 'AE', label: 'United Arab Emirates' },
  { id: 'IN', label: 'India' },
  { id: 'JP', label: 'Japan' },
  { id: 'AU', label: 'Australia' },
  { id: 'SG', label: 'Singapore' },
  { id: 'ZA', label: 'South Africa' },
  { id: 'CN', label: 'China' },
];

/** Amazon marketplaceStringId → country code (SP-API / Ads profiles). */
const MARKETPLACE_COUNTRY: Record<string, string> = {
  // North America + Brazil
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A2Q3Y263D00KWC: 'BR',
  // Europe, Middle East, India, Africa
  A1F83G8C2ARO7P: 'UK',
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  A1RKKUPIHCS9HS: 'ES',
  APJ6JRA9NG5V4: 'IT',
  A1805IZSGTT6HS: 'NL',
  AMEN7PMS3EDWL: 'BE',
  A28R8C7NBKEWEA: 'IE',
  A2NODRKZP88ZB9: 'SE',
  A1C3SOZRARQ6R3: 'PL',
  A33AVAJ2PDY3EV: 'TR',
  ARBP9OOSHTCHU: 'EG',
  A17E79C6D8DWNP: 'SA',
  A2VIGQ35RCS4UG: 'AE',
  A21TJRUUN4KGV: 'IN',
  AE08WJ6YKNBMC: 'ZA',
  // Far East
  A1VC38T7YXB528: 'JP',
  A39IBJ37TRP1C6: 'AU',
  A19VAU5U5O7RUS: 'SG',
};

const LABEL_OVERRIDES: Record<string, string> = {
  EU: 'Europe',
  ...Object.fromEntries(ADVERTISING_MARKETPLACE_COUNTRIES.map((c) => [c.id, c.label])),
};

/** Normalize aliases that appear in Ads docs or profile payloads. */
const COUNTRY_ALIASES: Record<string, string> = {
  GB: 'UK',
  UAE: 'AE',
};

const displayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

export function advertisingRegionLabel(code: string): string {
  const id = code.trim().toUpperCase();
  if (!id) return 'Unknown';
  if (LABEL_OVERRIDES[id]) return LABEL_OVERRIDES[id];
  try {
    const name = displayNames?.of(id);
    if (name && name !== id) return name;
  } catch {
    // Intl.DisplayNames rejects codes that are not region identifiers.
  }
  return id;
}

export function countryCodeToAdvertisingRegion(
  countryCode: string | null | undefined
): AdvertisingAccountRegion {
  const raw = countryCode?.trim().toUpperCase();
  if (!raw) return 'IN';
  return COUNTRY_ALIASES[raw] ?? raw;
}

/** Mapped marketplace only. Unknown or missing ids stay unresolved. */
export function knownMarketplaceAdvertisingRegion(
  marketplaceId: string | null | undefined
): AdvertisingAccountRegion | null {
  const id = marketplaceId?.trim();
  if (!id) return null;
  const country = MARKETPLACE_COUNTRY[id];
  if (!country) return null;
  return countryCodeToAdvertisingRegion(country);
}

export function marketplaceIdToAdvertisingRegion(
  marketplaceId: string | null | undefined
): AdvertisingAccountRegion {
  return knownMarketplaceAdvertisingRegion(marketplaceId) ?? 'IN';
}

export function amazonAccountRegion(row: LinkedAccountRow): AdvertisingAccountRegion {
  const country = row.countryCode?.trim();
  if (country) return countryCodeToAdvertisingRegion(country);
  return marketplaceIdToAdvertisingRegion(row.marketplaceId);
}

export function flipkartAccountRegion(account: FlipkartAdsAdAccount): AdvertisingAccountRegion {
  const marketplace = account.marketplace?.trim().toLowerCase();
  if (marketplace === 'us' || marketplace === 'united_states') return 'US';
  if (marketplace === 'eu' || marketplace === 'europe') return 'EU';
  return 'IN';
}

export type AdvertisingRegionGroup<T> = {
  id: AdvertisingAccountRegion;
  label: string;
  items: T[];
};

const PREFERRED_REGION_ORDER = ADVERTISING_MARKETPLACE_COUNTRIES.map((region) => region.id);

export function groupByAdvertisingRegion<T>(
  items: T[],
  resolveRegion: (item: T) => AdvertisingAccountRegion
): AdvertisingRegionGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const id = resolveRegion(item);
    const list = buckets.get(id);
    if (list) list.push(item);
    else buckets.set(id, [item]);
  }

  const ids = [...buckets.keys()].sort((a, b) => {
    const ai = PREFERRED_REGION_ORDER.indexOf(a);
    const bi = PREFERRED_REGION_ORDER.indexOf(b);
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return a.localeCompare(b);
  });

  return ids.map((id) => ({
    id,
    label: advertisingRegionLabel(id),
    items: buckets.get(id) ?? [],
  }));
}
